import React from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  FormControl,
  FormLabel,
} from "@mui/material";
import { useOutletContext, useNavigate } from "react-router-dom";
import * as yup from "yup";
import moment from "moment-timezone";
import { Form, Formik, getIn } from "formik";
import { Timestamp } from "firebase/firestore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { v4 as uuidv4 } from "uuid";

import { DateTimeForm } from "../EventBasicInfo/DateTimeForm";
import { BottomButtons } from "../EventBasicInfo/BottomButtons";
import ConfirmationDialog from "../../ConfirmationDialog";
import { FormPrompt } from "../../FormPrompt/FormPrompt";
import { rezoneMomentDateTime } from "../../../utils";
import {
  createTicketType,
  updateTicketType,
} from "../../../redux/features/adminEventsSlice";
import PriceFormatCustom from "../EventTickets/modals/PriceFormatCustom";

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  description: yup.string().required("Description is required"),
  requiresPromoCode: yup.boolean(),
  listingVisibility: yup
    .string()
    .oneOf(["everywhere", "eventOnly", "sessionOnly"])
    .required(),
  startsAtDate: yup
    .date("Invalid Date")
    .typeError("Invalid Date")
    .required("Start Date is required"),
  startsAtTime: yup
    .date("Invalid Time")
    .typeError("Invalid Time")
    .required("Start Time is required"),
  endsAtDate: yup
    .date("Invalid Date")
    .typeError("Invalid Date")
    .required("End Date is required"),
  endsAtTime: yup
    .date("Invalid Time")
    .typeError("Invalid Time")
    .required("End Time is required"),
  capacity: yup
    .number()
    .typeError("Must be a valid number")
    .when(["limit"], (limit, schema) => {
      return limit[0] === "limited"
        ? schema
            .positive("Must be a positive number")
            .integer("Must be a whole number")
            .required("Capacity is required")
            .min(1, "Must be greater than 0")
        : schema.nullable(true);
    }),
  capacityPerPerson: yup
    .number()
    .typeError("Must be a valid number")
    .positive("Must be a positive number")
    .integer("Must be a whole number")
    .required("Per-order limit is required")
    .min(1, "Must be greater than 0"),
  fees: yup.array().of(
    yup.object({
      enabled: yup.boolean(),
      name: yup.string().required("Fee name is required"),
      amountType: yup.string().required("Amount type is required"),
      amountUsdCents: yup.number().when(["amountType"], {
        is: "usdCents",
        then: (schema) =>
          schema
            .typeError("Must be a valid number")
            .positive("Must be a positive number")
            .required("Dollar amount is required")
            .moreThan(0, "Must be greater than 0"),
        otherwise: (schema) => schema.nullable(true),
      }),
      amountPercentage: yup.number().when(["amountType"], {
        is: "percentage",
        then: (schema) =>
          schema
            .typeError("Must be a valid number")
            .positive("Must be a positive number")
            .required("Percentage is required")
            .min(0.01, "Must be greater than 0"),
        otherwise: (schema) => schema.nullable(true),
      }),
      timespanApplicationType: yup
        .string()
        .required("Fee application type is required"),
      startsAtDate: yup.date().when(["timespanApplicationType"], {
        is: "dateRange",
        then: (schema) =>
          schema.required("Fee start date is required for date range fees"),
        otherwise: (schema) => schema.nullable(true),
      }),
      endsAtDate: yup.date().when(["timespanApplicationType"], {
        is: "dateRange",
        then: (schema) =>
          schema.required("Fee end date is required for date range fees"),
        otherwise: (schema) => schema.nullable(true),
      }),
    }),
  ),
});

export const buildValuesPayload = (values, timezone) => {
  let newValues = { ...values };

  const { startsAtDate, startsAtTime, endsAtDate, endsAtTime } = newValues;

  const timeValues = {
    availabilityStartsAt: Timestamp.fromDate(
      rezoneMomentDateTime(startsAtDate, startsAtTime, timezone).toDate(),
    ),
    availabilityEndsAt: Timestamp.fromDate(
      rezoneMomentDateTime(endsAtDate, endsAtTime, timezone).toDate(),
    ),
  };

  const statsPayload = {
    capacity: values.capacity ? Number(values.capacity) : null,
    capacityPerPerson: values.capacityPerPerson
      ? Number(values.capacityPerPerson)
      : null,
  };

  const feesPayload = values.fees.map((fee) => {
    let amount;
    switch (fee.amountType) {
      case "usdCents":
        amount = Math.round(Number(fee.amountUsdCents) * 100);
        break;
      case "percentage":
        amount = Math.round(Number(fee.amountPercentage) * 100);
        break;
      default:
        amount = Math.round(Number(fee.amount) * 100);
        break;
    }
    return {
      key: fee.key || uuidv4(),
      name: fee.name,
      amount,
      amountType: fee.amountType,
      timespanApplicationType: fee.timespanApplicationType,
      startsAt:
        fee.timespanApplicationType === "dateRange"
          ? Timestamp.fromDate(fee.startsAtDate.toDate())
          : null,
      endsAt:
        fee.timespanApplicationType === "dateRange"
          ? Timestamp.fromDate(fee.endsAtDate.toDate())
          : null,
      enabled: fee.enabled !== false,
    };
  });

  delete newValues.startsAtDate;
  delete newValues.startsAtTime;
  delete newValues.endsAtDate;
  delete newValues.endsAtTime;
  delete newValues.limit;
  delete newValues.capacity;
  delete newValues.fees;

  newValues = {
    ...newValues,
    ...timeValues,
    stats: statsPayload,
    fees: feesPayload,
  };

  return { ticketType: newValues };
};

const ticketTypeValues = (ticketType, timezone) => {
  const {
    name,
    description,
    requiresPromoCode,
    listingVisibility,
    availabilityStartsAt,
    availabilityEndsAt,
    usdCents,
    key,
    stats,
    fees = [],
  } = ticketType || {};

  const startsAtDate = availabilityStartsAt
    ? moment.tz(availabilityStartsAt.toDate(), timezone)
    : null;
  const startsAtTime = availabilityStartsAt
    ? moment.tz(availabilityStartsAt.toDate(), timezone)
    : null;
  const endsAtDate = availabilityEndsAt
    ? moment.tz(availabilityEndsAt.toDate(), timezone)
    : null;
  const endsAtTime = availabilityEndsAt
    ? moment.tz(availabilityEndsAt.toDate(), timezone)
    : null;

  const { capacity, capacityPerPerson } = stats || {};
  const limit = capacity ? "limited" : "unlimited";

  return {
    name,
    description,
    requiresPromoCode,
    listingVisibility: listingVisibility ?? "everywhere",
    usdCents,
    startsAtDate,
    startsAtTime,
    endsAtDate,
    endsAtTime,
    key,
    capacity,
    capacityPerPerson,
    limit,
    fees: fees.map((fee) => {
      let amountUsdCents = null;
      let amountPercentage = null;
      switch (fee.amountType) {
        case "usdCents":
          amountUsdCents = fee.amount;
          break;
        case "percentage":
          amountPercentage = fee.amount;
          break;
        default:
          amountUsdCents = fee.amount;
          break;
      }
      return {
        key: fee.key || uuidv4(),
        enabled: fee.enabled !== false,
        name: fee.name || "",
        amountType: fee.amountType || "usdCents",
        amountUsdCents: amountUsdCents ? (amountUsdCents / 100).toFixed(2) : "",
        amountPercentage: amountPercentage
          ? (amountPercentage / 100).toFixed(2)
          : "",
        timespanApplicationType: fee.timespanApplicationType || "always",
        startsAtDate: fee.startsAt
          ? moment.tz(fee.startsAt.toDate(), timezone)
          : null,
        endsAtDate: fee.endsAt
          ? moment.tz(fee.endsAt.toDate(), timezone)
          : null,
      };
    }),
  };
};

export const EditTicketTypeForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState(false);

  const { event, editingTicketType, creatingNew } = useOutletContext();
  const { id: eventId, timezone } = event;

  const { stats } = editingTicketType || {};

  const handleSubmit = async (values, submitProps) => {
    await submitProps.resetForm({ values });
    const { ticketType } = buildValuesPayload(values, timezone);
    if (creatingNew) {
      await dispatch(createTicketType({ eventId, values: ticketType }));
    } else {
      await dispatch(updateTicketType({ eventId, values: ticketType }));
    }
    navigate("../");
  };

  const initialValues = React.useMemo(() => {
    return ticketTypeValues(editingTicketType, timezone);
  }, [editingTicketType, timezone]);

  const startDateQuickSelects = React.useMemo(() => {
    const options = [
      {
        label: "Now",
        getMoment: () => moment.tz(moment(), timezone),
      },
    ];
    if (event.startsAt) {
      const eventStart = moment.tz(event.startsAt.toDate(), timezone);
      options.push(
        {
          label: "Day of the event",
          getMoment: () => eventStart.clone().startOf("day"),
        },
        {
          label: "One month before the event",
          getMoment: () =>
            eventStart.clone().subtract(30, "days").startOf("day"),
        },
        {
          label: "One week before the event",
          getMoment: () =>
            eventStart.clone().subtract(7, "days").startOf("day"),
        },
      );
    }
    return options;
  }, [event.startsAt, timezone]);

  const endDateQuickSelects = React.useMemo(() => {
    const options = [];
    if (event.startsAt) {
      const eventStart = moment.tz(event.startsAt.toDate(), timezone);
      options.push(
        {
          label: "Start of the event",
          getMoment: () => eventStart.clone(),
        },
        {
          label: "Day of the event",
          getMoment: () => eventStart.clone().startOf("day"),
        },
        {
          label: "One week before the event",
          getMoment: () =>
            eventStart.clone().subtract(7, "days").startOf("day"),
        },
      );
    }
    if (event.endsAt) {
      const eventEnd = moment.tz(event.endsAt.toDate(), timezone);
      options.unshift({
        label: "End of the event",
        getMoment: () => eventEnd.clone(),
      });
    }
    return options;
  }, [event.startsAt, event.endsAt, timezone]);

  if (!editingTicketType || !stats) {
    return;
  }

  return (
    <Box sx={{ paddingBottom: "90px" }}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, submitProps) => {
          const submitValues = { ...values };

          Object.keys(submitValues).forEach((key) =>
            submitValues[key] === undefined ? delete submitValues[key] : {},
          );

          await handleSubmit(submitValues, submitProps);
        }}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          isValid,
          dirty,
          setValues,
          setFieldValue,
          handleReset,
          submitForm,
        }) => {
          const nameKey = "name";
          const touchedName = getIn(touched, nameKey);
          const errorName = getIn(errors, nameKey);
          const descriptionKey = "description";
          const touchedDescription = getIn(touched, descriptionKey);
          const errorDescription = getIn(errors, descriptionKey);
          const requiresPromoCodeKey = "requiresPromoCode";
          const requiresPromoCodeValue =
            getIn(values, requiresPromoCodeKey) || false;
          const usdCentsKey = "usdCents";
          const touchedUsdCents = getIn(touched, usdCentsKey);
          const errorUsdCents = getIn(errors, usdCentsKey);
          let usdCentsValue = ((values[usdCentsKey] || 0) / 100).toFixed(2);
          const capacityPerPersonKey = "capacityPerPerson";
          const capacityPerPersonValue = getIn(values, capacityPerPersonKey);
          const touchedCapacityPerPerson = getIn(touched, capacityPerPersonKey);
          const errorCapacityPerPerson = getIn(errors, capacityPerPersonKey);

          return (
            <Form noValidate autoComplete="off">
              <FormPrompt hasUnsavedChanges={dirty} />
              <ConfirmationDialog
                open={confirmationDialogOpen}
                title={"Close?"}
                message={
                  "You have unsaved changes. Save or discard them to continue."
                }
                buttonDefsOverride={[
                  {
                    text: "Cancel",
                    onClick: () => {
                      setConfirmationDialogOpen(false);
                    },
                  },
                  {
                    text: "Discard",
                    onClick: async () => {
                      await handleReset();
                      navigate("../");
                    },
                  },
                  {
                    text: "Save",
                    type: "submit",
                    onClick: () => {
                      setConfirmationDialogOpen(false);
                      submitForm();
                    },
                  },
                ]}
              />
              <Box>
                <Box
                  width={"100%"}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"20px"}
                  marginTop={"40px"}
                  marginBottom={"20px"}
                >
                  <Typography variant="h4">
                    {creatingNew ? "New Ticket Type" : "Edit Ticket Type"}
                  </Typography>
                  <Button
                    onClick={() => {
                      if (dirty) {
                        setConfirmationDialogOpen(true);
                      } else {
                        navigate("../");
                      }
                    }}
                  >
                    Close
                  </Button>
                </Box>
                <TextField
                  required
                  sx={{ width: "100%", marginTop: "20px" }}
                  id={nameKey}
                  name={nameKey}
                  label="Name"
                  value={values[nameKey] || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touchedName && errorName)}
                  helperText={touchedName && errorName ? errorName : " "}
                />
                <TextField
                  required
                  sx={{ width: "100%", marginTop: "20px" }}
                  id={descriptionKey}
                  name={descriptionKey}
                  label="Description"
                  value={values[descriptionKey] || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touchedDescription && errorDescription)}
                  helperText={
                    touchedDescription && errorDescription
                      ? errorDescription
                      : " "
                  }
                />
                <FormControlLabel
                  sx={{ marginTop: "0px" }}
                  control={
                    <Checkbox
                      id={requiresPromoCodeKey}
                      name={requiresPromoCodeKey}
                      checked={requiresPromoCodeValue}
                      onChange={(e) => {
                        setFieldValue(requiresPromoCodeKey, e.target.checked);
                      }}
                    />
                  }
                  label={"Hidden ticket (reveal with promo code)"}
                />

                <FormControl
                  component="fieldset"
                  variant="standard"
                  sx={{ width: "100%", mt: 3 }}
                  data-testid="listing-visibility-group"
                >
                  <FormLabel component="legend">Show this ticket on</FormLabel>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Control whether buyers see this type on the main event page,
                    on session pages, or both.
                  </Typography>
                  <RadioGroup
                    name="listingVisibility"
                    value={values.listingVisibility ?? "everywhere"}
                    onChange={(e) =>
                      setFieldValue("listingVisibility", e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="everywhere"
                      control={<Radio />}
                      label="Everywhere (event page and session pages)"
                    />
                    <FormControlLabel
                      value="eventOnly"
                      control={<Radio />}
                      label="Event page only (not on session pages)"
                    />
                    <FormControlLabel
                      value="sessionOnly"
                      control={<Radio />}
                      label="Session pages only (not on main event page)"
                    />
                  </RadioGroup>
                </FormControl>

                <DateTimeForm
                  formikProps={{
                    values,
                    setFieldValue,
                    touched,
                    errors,
                  }}
                  label={"Ticket Availability Time Window"}
                  startDateQuickSelects={startDateQuickSelects}
                  endDateQuickSelects={endDateQuickSelects}
                />
                <Typography marginTop={"20px"} marginBottom={"10px"}>
                  Ticket Capacity
                </Typography>
                <RadioGroup
                  aria-labelledby={`limit-label`}
                  value={values.limit}
                  name={"limit"}
                  onChange={(e) => {
                    setFieldValue("capacity", null);
                    handleChange(e);
                  }}
                >
                  <FormControlLabel
                    value="unlimited"
                    control={<Radio />}
                    label="Unlimited"
                  />
                  <Box display={"flex"} gap={"10px"} justifyItems={"center"}>
                    <FormControlLabel
                      value="limited"
                      control={<Radio />}
                      label="Limit capacity to:"
                    />
                    <TextField
                      sx={{ width: "200px", marginBottom: "-20px" }}
                      required
                      disabled={values.limit === "unlimited"}
                      id={"capacity"}
                      name={"capacity"}
                      value={values.capacity || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.capacity && errors.capacity)}
                      helperText={
                        touched.capacity && errors.capacity
                          ? errors.capacity
                          : " "
                      }
                    />
                  </Box>
                </RadioGroup>

                <Box>
                  <TextField
                    required
                    sx={{ width: "150px", marginTop: "40px" }}
                    id={capacityPerPersonKey}
                    name={capacityPerPersonKey}
                    label="Per-order limit"
                    value={capacityPerPersonValue || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      touchedCapacityPerPerson && errorCapacityPerPerson,
                    )}
                    helperText={
                      touchedCapacityPerPerson && errorCapacityPerPerson
                        ? errorCapacityPerPerson
                        : " "
                    }
                  />
                </Box>
                <TextField
                  required
                  sx={{ width: "150px", marginTop: "40px" }}
                  id={usdCentsKey}
                  name={usdCentsKey}
                  label="Ticket Price"
                  value={usdCentsValue}
                  onChange={(event) => {
                    let value = parseInt(parseFloat(event.target.value) * 100);
                    value = Number.isNaN(value) ? 0 : value;
                    setFieldValue(usdCentsKey, value);
                  }}
                  onBlur={handleBlur}
                  error={Boolean(touchedUsdCents && errorUsdCents)}
                  helperText={
                    touchedUsdCents && errorUsdCents ? errorUsdCents : " "
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    inputComponent: PriceFormatCustom,
                  }}
                />

                <Box sx={{ mt: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography marginTop={"20px"} marginBottom={"10px"}>
                        Organizer Fees
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Organizer fees are applied after any promo code
                        discounts and are collected by the event organizer.
                        These fees can be used to cover service charges,
                        processing fees, or other event-specific costs.
                      </Typography>
                    </Box>
                    <Button
                      startIcon={<AddIcon />}
                      sx={{ minWidth: "120px", flexShrink: 0 }}
                      onClick={() => {
                        const currentFees = values.fees || [];
                        setFieldValue("fees", [
                          ...currentFees,
                          {
                            key: uuidv4(),
                            enabled: true,
                            name: "",
                            amountType: "usdCents",
                            amountUsdCents: "",
                            amountPercentage: "",
                            timespanApplicationType: "always",
                            startsAtDate: null,
                            endsAtDate: null,
                          },
                        ]);
                      }}
                    >
                      Add Fee
                    </Button>
                  </Box>

                  <DragDropContext
                    onDragEnd={(result) => {
                      if (!result.destination) return;
                      const newFees = Array.from(values.fees);
                      const [removed] = newFees.splice(result.source.index, 1);
                      newFees.splice(result.destination.index, 0, removed);
                      setFieldValue("fees", newFees);
                    }}
                  >
                    <Droppable droppableId="fees-droppable">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {(values.fees || []).map((fee, index) => (
                            <Draggable
                              key={fee.key || index}
                              draggableId={fee.key || String(index)}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={{
                                    userSelect: "none",
                                    margin: "0 0 16px 0",
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <Paper sx={{ p: 2, mb: 2 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                      }}
                                    >
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{
                                          cursor: "grab",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <UnfoldMoreIcon color="primary" />
                                      </Box>
                                      <IconButton
                                        onClick={() => {
                                          const newFees = [...values.fees];
                                          newFees.splice(index, 1);
                                          setFieldValue("fees", newFees);
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>

                                    <TextField
                                      required
                                      sx={{ width: "100%", mb: 2 }}
                                      id={`fees.${index}.name`}
                                      name={`fees.${index}.name`}
                                      label="Fee Name"
                                      value={fee.name}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={Boolean(
                                        getIn(touched, `fees.${index}.name`) &&
                                          getIn(errors, `fees.${index}.name`),
                                      )}
                                      helperText={
                                        getIn(touched, `fees.${index}.name`) &&
                                        getIn(errors, `fees.${index}.name`)
                                          ? getIn(errors, `fees.${index}.name`)
                                          : " "
                                      }
                                    />

                                    <FormControl sx={{ mb: 2 }}>
                                      <FormLabel>Fee Amount Type</FormLabel>
                                      <RadioGroup
                                        value={fee.amountType}
                                        onChange={(e) => {
                                          setFieldValue(
                                            `fees.${index}.amountType`,
                                            e.target.value,
                                          );
                                          if (e.target.value === "usdCents") {
                                            setFieldValue(
                                              `fees.${index}.amountUsdCents`,
                                              "",
                                            );
                                            setFieldValue(
                                              `fees.${index}.amountPercentage`,
                                              null,
                                            );
                                          } else {
                                            setFieldValue(
                                              `fees.${index}.amountPercentage`,
                                              "",
                                            );
                                            setFieldValue(
                                              `fees.${index}.amountUsdCents`,
                                              null,
                                            );
                                          }
                                        }}
                                      >
                                        <Box
                                          display={"flex"}
                                          gap={"10px"}
                                          alignItems={"center"}
                                        >
                                          <FormControlLabel
                                            value="usdCents"
                                            control={<Radio />}
                                            label="Dollar amount:"
                                            sx={{ width: "150px" }}
                                          />
                                          <TextField
                                            sx={{ width: "200px" }}
                                            required={
                                              fee.amountType === "usdCents"
                                            }
                                            disabled={
                                              fee.amountType === "percentage"
                                            }
                                            id={`fees.${index}.amountUsdCents`}
                                            name={`fees.${index}.amountUsdCents`}
                                            value={fee.amountUsdCents ?? ""}
                                            onChange={(event) => {
                                              let value = parseInt(
                                                parseFloat(event.target.value) *
                                                  100,
                                              );
                                              value = Number.isNaN(value)
                                                ? 0
                                                : value;
                                              setFieldValue(
                                                `fees.${index}.amountUsdCents`,
                                                value / 100,
                                              );
                                            }}
                                            onBlur={handleBlur}
                                            error={
                                              fee.amountType === "usdCents" &&
                                              Boolean(
                                                getIn(
                                                  touched,
                                                  `fees.${index}.amountUsdCents`,
                                                ) &&
                                                  getIn(
                                                    errors,
                                                    `fees.${index}.amountUsdCents`,
                                                  ),
                                              )
                                            }
                                            helperText={
                                              fee.amountType === "usdCents" &&
                                              getIn(
                                                touched,
                                                `fees.${index}.amountUsdCents`,
                                              ) &&
                                              getIn(
                                                errors,
                                                `fees.${index}.amountUsdCents`,
                                              )
                                                ? getIn(
                                                    errors,
                                                    `fees.${index}.amountUsdCents`,
                                                  )
                                                : " "
                                            }
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  $
                                                </InputAdornment>
                                              ),
                                              inputComponent: PriceFormatCustom,
                                            }}
                                          />
                                        </Box>
                                        <Box
                                          display={"flex"}
                                          gap={"10px"}
                                          alignItems={"center"}
                                          mt={2}
                                        >
                                          <FormControlLabel
                                            value="percentage"
                                            control={<Radio />}
                                            label="Percentage:"
                                            sx={{ width: "150px" }}
                                          />
                                          <TextField
                                            sx={{ width: "200px" }}
                                            required={
                                              fee.amountType === "percentage"
                                            }
                                            disabled={
                                              fee.amountType === "usdCents"
                                            }
                                            id={`fees.${index}.amountPercentage`}
                                            name={`fees.${index}.amountPercentage`}
                                            value={fee.amountPercentage ?? ""}
                                            onChange={(event) => {
                                              let value = parseInt(
                                                parseFloat(event.target.value) *
                                                  100,
                                              );
                                              value = Number.isNaN(value)
                                                ? 0
                                                : value;
                                              setFieldValue(
                                                `fees.${index}.amountPercentage`,
                                                value / 100,
                                              );
                                            }}
                                            onBlur={handleBlur}
                                            error={
                                              fee.amountType === "percentage" &&
                                              Boolean(
                                                getIn(
                                                  touched,
                                                  `fees.${index}.amountPercentage`,
                                                ) &&
                                                  getIn(
                                                    errors,
                                                    `fees.${index}.amountPercentage`,
                                                  ),
                                              )
                                            }
                                            helperText={
                                              fee.amountType === "percentage" &&
                                              getIn(
                                                touched,
                                                `fees.${index}.amountPercentage`,
                                              ) &&
                                              getIn(
                                                errors,
                                                `fees.${index}.amountPercentage`,
                                              )
                                                ? getIn(
                                                    errors,
                                                    `fees.${index}.amountPercentage`,
                                                  )
                                                : " "
                                            }
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  %
                                                </InputAdornment>
                                              ),
                                              inputComponent: PriceFormatCustom,
                                            }}
                                          />
                                        </Box>
                                      </RadioGroup>
                                    </FormControl>

                                    <Box sx={{ mb: 2 }}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={fee.enabled}
                                            onChange={(e) => {
                                              setFieldValue(
                                                `fees.${index}.enabled`,
                                                e.target.checked,
                                              );
                                            }}
                                          />
                                        }
                                        label="Enabled"
                                      />
                                    </Box>

                                    <RadioGroup
                                      value={fee.timespanApplicationType}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `fees.${index}.timespanApplicationType`,
                                          e.target.value,
                                        );
                                        if (e.target.value === "always") {
                                          setFieldValue(
                                            `fees.${index}.startsAtDate`,
                                            null,
                                          );
                                          setFieldValue(
                                            `fees.${index}.endsAtDate`,
                                            null,
                                          );
                                        }
                                      }}
                                    >
                                      <FormControlLabel
                                        value="always"
                                        control={<Radio />}
                                        label="Always Apply Fee"
                                      />
                                      <FormControlLabel
                                        value="dateRange"
                                        control={<Radio />}
                                        label="Apply Fee During Date Range"
                                      />
                                    </RadioGroup>

                                    {fee.timespanApplicationType ===
                                      "dateRange" && (
                                      <Box sx={{ mt: 2 }}>
                                        <DateTimeForm
                                          formikProps={{
                                            values: {
                                              startsAtDate: fee.startsAtDate,
                                              startsAtTime: fee.startsAtDate,
                                              endsAtDate: fee.endsAtDate,
                                              endsAtTime: fee.endsAtDate,
                                            },
                                            setFieldValue: (field, value) => {
                                              if (
                                                field === "startsAtDate" ||
                                                field === "startsAtTime"
                                              ) {
                                                setFieldValue(
                                                  `fees.${index}.startsAtDate`,
                                                  value,
                                                );
                                              } else if (
                                                field === "endsAtDate" ||
                                                field === "endsAtTime"
                                              ) {
                                                setFieldValue(
                                                  `fees.${index}.endsAtDate`,
                                                  value,
                                                );
                                              }
                                            },
                                            touched: {
                                              startsAtDate: getIn(
                                                touched,
                                                `fees.${index}.startsAtDate`,
                                              ),
                                              startsAtTime: getIn(
                                                touched,
                                                `fees.${index}.startsAtDate`,
                                              ),
                                              endsAtDate: getIn(
                                                touched,
                                                `fees.${index}.endsAtDate`,
                                              ),
                                              endsAtTime: getIn(
                                                touched,
                                                `fees.${index}.endsAtDate`,
                                              ),
                                            },
                                            errors: {
                                              startsAtDate: getIn(
                                                errors,
                                                `fees.${index}.startsAtDate`,
                                              ),
                                              startsAtTime: getIn(
                                                errors,
                                                `fees.${index}.startsAtDate`,
                                              ),
                                              endsAtDate: getIn(
                                                errors,
                                                `fees.${index}.endsAtDate`,
                                              ),
                                              endsAtTime: getIn(
                                                errors,
                                                `fees.${index}.endsAtDate`,
                                              ),
                                            },
                                          }}
                                          label="Fee Date Range"
                                        />
                                      </Box>
                                    )}
                                  </Paper>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Box>
              </Box>

              <BottomButtons
                show={creatingNew || dirty}
                handleDiscard={() => {
                  if (creatingNew) {
                    navigate("../");
                  } else {
                    handleReset();
                  }
                }}
              />
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};
